import { useState, useEffect } from 'react';
import { useAuthContext } from '../../components/AuthProvider';

export const usePosOperatorShop = () => {
  const [shops, setShops] = useState(
    JSON.parse(sessionStorage.getItem('tos-shops')) || []
  );
  const [selectedShop, setSelectedShop] = useState(
    JSON.parse(sessionStorage.getItem('tos-selectedShop')) || null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const { loginUser, loginRole, token } = useAuthContext();
  const userId = loginUser?.id;
  const isPosOperator = loginRole.includes('POS Operator');
  const designationId = '00010000-0000-0000-0000-000000000003';

  useEffect(() => {
    const controller = new AbortController();
    const fetchPosOperatorShops = async () => {
      const storedShops = JSON.parse(sessionStorage.getItem('tos-shops'));
      const storedSelectedShop = JSON.parse(sessionStorage.getItem('tos-selectedShop'));
      if (storedShops?.length > 0) {
        setShops(storedShops);
        if (storedSelectedShop) {
          setSelectedShop(storedSelectedShop);
        }
        return;
      }

      if (!isPosOperator) {
        setError('User is not a POS Operator');
        return;
      }
      if (!userId) {
        setError('User ID not found in authentication context');
        return;
      }
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      setLoading(true);
      try {
        const response = await fetch(
          `https://api.synergytoss.net/api/v1/employeedesignations/designation/${designationId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const designations = await response.json();
        if (!Array.isArray(designations)) {
          throw new Error('Invalid response format: designations is not an array');
        }

        const matchingDesignations = designations.filter(
          (designation) => designation.employee?.userId === userId
        );

        if (matchingDesignations.length === 0) {
          throw new Error('No shops found for the provided user ID with POS Operator designation');
        }

        const shopList = matchingDesignations.map((designation) => {
          const { shop } = designation;
          if (!shop?.id || !shop?.name) {
            throw new Error('Shop details are incomplete in the response');
          }
          return {
            shopId: shop.id,
            shopName: shop.name,
            shopLogo: shop.logo,
            businessId: shop.businessId,
            locationAddress: shop.locationAddress, // Fixed
            email: shop.email,
            telephone: shop.telephone,
          };
        });

        setShops(shopList);
        sessionStorage.setItem('tos-shops', JSON.stringify(shopList));

        if (!storedSelectedShop && shopList.length > 0) {
          const defaultShop = shopList[0];
          setSelectedShop(defaultShop);
          sessionStorage.setItem('tos-selectedShop', JSON.stringify(defaultShop));
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosOperatorShops();
    return () => controller.abort();
  }, [userId, isPosOperator, token, designationId]);

  const selectShop = (shopId) => {
    const shop = shops.find((s) => s.shopId === shopId);
    if (shop) {
      setSelectedShop(shop);
      sessionStorage.setItem('tos-selectedShop', JSON.stringify(shop));
    }
  };

  return { shops, selectedShop, selectShop, loading, error };
};